import React from 'react';
import { DataTable } from '@frontend/app/refresh-components/DataTableV2/Table';
import { SortingState } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronUpIcon, ArrowDownArrowUpIcon } from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@/shadcn/components/ui/button';
import { GetTierPerformanceStatsForOffer_getTierPerformanceStatsForOffer } from '../../queries/types/GetTierPerformanceStatsForOffer';
import { AutomatedPayoutType } from '../../types/globalTypes';

interface Props {
    data?: GetTierPerformanceStatsForOffer_getTierPerformanceStatsForOffer[];
}

interface TransformedTierData {
    tier: string;
    payout: string;
    totalMembers: number;
    newMembers: number;
    isNewMembersUp: boolean;
    icon?: string;
    automationThreshold: number;
}

// Transform GraphQL data to display format
const transformTierData = (data: GetTierPerformanceStatsForOffer_getTierPerformanceStatsForOffer[]): TransformedTierData[] => {
    // Find the highest automation threshold
    const highestThreshold = Math.max(...data.map((item) => item.automationThreshold));

    return data.map((item) => {
        const { automationThreshold, offerPayout, totalMembersInTier, newMembersInTier } = item;

        // Create payout display string
        let payoutDisplay = offerPayout.label;
        if (offerPayout.flatPayout && offerPayout.percentPayout) {
            payoutDisplay += ` ($${offerPayout.flatPayout} + ${offerPayout.percentPayout}%)`;
        } else if (offerPayout.flatPayout) {
            payoutDisplay += ` ($${offerPayout.flatPayout})`;
        } else if (offerPayout.percentPayout) {
            payoutDisplay += ` (${offerPayout.percentPayout}%)`;
        }

        const isNewMembersUp = newMembersInTier > 0;
        const icon = automationThreshold === highestThreshold ? '👑' : undefined;

        return {
            tier: `${item.automationType === AutomatedPayoutType.CONVERSION ? 'Conversions' : 'Sales'} >= ${item.automationType === AutomatedPayoutType.SALE ? '$' : ''}${automationThreshold}`,
            payout: payoutDisplay,
            totalMembers: totalMembersInTier,
            newMembers: newMembersInTier,
            isNewMembersUp,
            icon,
            automationThreshold,
        };
    }).sort((a, b) => a.automationThreshold - b.automationThreshold); // Sort by threshold in ascending order
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const SortButton = ({ column, children }: { column: any; children: React.ReactNode }) => {
    const isSorted = column.getIsSorted();
    const [isHovering, setIsHovering] = React.useState(false);

    const handleSort = () => {
        if (isSorted === 'asc') {
            column.toggleSorting(true);
        } else if (isSorted === 'desc') {
            column.clearSorting();
        } else {
            column.toggleSorting(false);
        }
    };

    return (
      <Button
        variant="ghost"
        className="px-2 hover:bg-transparent w-full"
        onClick={handleSort}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div className="flex w-full justify-between items-center">
          <span>{children}</span>
          <span className="h-3 w-3 flex items-center justify-center">
            {isSorted === 'asc' && <ChevronUpIcon className="h-3 w-3" />}
            {isSorted === 'desc' && <ChevronDownIcon className="h-3 w-3" />}
            {!isSorted && isHovering && <ArrowDownArrowUpIcon className="h-3 w-3" />}
          </span>
        </div>
      </Button>
    );
};

const columns = [
    {
        accessorKey: 'tier',
        header: ({ column }) => <SortButton column={column}>Tier</SortButton>,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span>{row.original.tier}</span>
            {row.original.icon && <span role="img" aria-label="crown">{row.original.icon}</span>}
          </div>
        ),
        sortable: true,
    },
    {
        accessorKey: 'payout',
        header: ({ column }) => <SortButton column={column}>Payout</SortButton>,
        cell: ({ row }) => row.original.payout,
        sortable: true,
    },
    {
        accessorKey: 'totalMembers',
        header: ({ column }) => <SortButton column={column}>Total members in tier</SortButton>,
        cell: ({ row }) => row.original.totalMembers,
        sortable: true,
    },
    {
        accessorKey: 'newMembers',
        header: ({ column }) => <SortButton column={column}>New members in this tier (past 7 days)</SortButton>,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            {row.original.newMembers}
            {row.original.isNewMembersUp && (<span className="text-green-600">↑</span>)}
          </div>
        ),
        sortable: true,
    },
];

export function TiersPerformanceTable({ data }: Props) {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [showAll, setShowAll] = React.useState(false);

    // Transform the data to the display format
    const transformedData = React.useMemo(() => {
        if (!data || data.length === 0) return [];
        return transformTierData(data);
    }, [data]);

    const tableData = React.useMemo(() => transformedData, [transformedData]);
    const sortedData = React.useMemo(() => {
        if (!sorting.length) return tableData;
        const [{ id, desc }] = sorting;
        return [...tableData].sort((a, b) => {
            const aValue = a[id as keyof typeof a];
            const bValue = b[id as keyof typeof b];
            if (aValue === bValue) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return desc ? bValue - aValue : aValue - bValue;
            }
            return desc
                ? String(bValue).localeCompare(String(aValue))
                : String(aValue).localeCompare(String(bValue));
        });
    }, [tableData, sorting]);

    const visibleData = showAll ? sortedData : sortedData.slice(0, 3);
    const totalCount = sortedData.length;

    return (
      <div className="border-t border-l border-r rounded-lg">
        <DataTable
          columns={columns}
          data={visibleData}
          enableColumnResizing={false}
          getRowId={(row) => `${row.tier}-${row.totalMembers}`}
          className="min-w-[900px]"
          onSortingChange={setSorting}
        />
        {totalCount > 3 && (
        <div className="flex justify-center mt-4">
          <button
            className="relative text-sm font-medium text-primary underline underline-offset-4 flex items-center"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? 'Show Less' : 'View All Tiers'}
          </button>
        </div>
            )}
      </div>
    );
}

export default TiersPerformanceTable;
